import React, { useState } from "react";

import { useGetDashboardHistory } from "api/api-hooks/dashboards-hooks";
import { DashboardHistoryDto } from "api/model/dashboard/Dashboard";
import DataTable from "components/basic/data-table/DataTable";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { Button } from "primereact/button";
import { DataTableStateEvent } from "primereact/datatable";
import { useDisplayTimezone } from "hooks/useTimezone";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { DATE_PATTERN_SECONDS } from "utils/constants";
import { fmtDatetime } from "utils/time-utils";

type DashboardHistoryTableProps = {
  closeDialog?: () => void;
};

const ROWS = 10;

const DashboardHistoryTable: React.FC<DashboardHistoryTableProps> = ({ closeDialog }) => {
  const displayTz = useDisplayTimezone();

  const [pagination, setPagination] = useState({ first: 0, rows: ROWS });
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const setFromHistory = useDashboardStore(state => state.setFromHistory);

  const { data, isLoading, isFetching } = useGetDashboardHistory(
    {
      dashboardId: dashboard!.id,
      take: pagination.rows,
      skip: pagination.first,
    },
    {
      keepPreviousData: true,
    }
  );

  const renderActions = (rowData: DashboardHistoryDto) => {
    const handleDashboardLoad = () => {
      if (!dashboard) return;
      const { dashboardTabs, dashboardTabsJs, reportOptions, tabGroups } = rowData;
      setDashboard({ ...dashboard, dashboardTabs, dashboardTabsJs, reportOptions, tabGroups });
      setFromHistory(true);
      closeDialog?.();
    };

    return <Button label={T("action.load")} text onClick={handleDashboardLoad} className="p-0" />;
  };

  const onPage = (e: DataTableStateEvent) => {
    setPagination({ first: e.first, rows: e.rows });
  };

  const dashboardColumns: TableColumn<DashboardHistoryDto>[] = [
    {
      field: "changeOn",
      header: T("word.updated-at"),
      columnKey: "changeOn",
      body: rowData => fmtDatetime(rowData.changeOn, displayTz, DATE_PATTERN_SECONDS),
    },
    {
      field: "changeBy",
      header: T("word.updated-by"),
      columnKey: "changeBy",
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      body: renderActions,
    },
  ];

  const tableConfig = {
    loading: isLoading || isFetching,
    lazy: true,
    rows: pagination.rows,
    first: pagination.first,
    onPage,
    totalRecords: data?.pageInfo.total ?? 0,
  };

  return <DataTable columns={dashboardColumns} data={data?.items ?? []} tableConfig={tableConfig} />;
};

export default DashboardHistoryTable;
